import type {
  CheckBoxTreeModelType,
  CheckBoxTreeUpdateDataEvent,
  InputSearchModelType
} from '@c4-links/vue3-links-components';
import type { FilterDefinitionValue } from '@carrefour-gcs/bff-sdk';
import { computed, ref, watch, type Ref } from 'vue';
import { TreeFilterSelection } from '#domain/core/filterDefinition/treeFilterSelection';
import type { TreeSelectionType } from '#domain/core/filterDefinition/types/treeSelection.type';

const isSearchActive = (input: InputSearchModelType | undefined) =>
  !!input?.searchValue && !(Array.isArray(input.searchValue) && input.searchValue.length === 0);

/**
 * Drives the CheckBoxTree of a tree filter; the selection logic itself lives in TreeFilterSelection.
 */
export function useCheckboxTreeSelection(deps: {
  isEnabled: Ref<boolean>;
  fieldOptions: Ref<FilterDefinitionValue[] | undefined>;
  initialValue: Ref<unknown>;
  searchData: Readonly<Ref<FilterDefinitionValue[] | undefined>>;
  searchInput: Ref<InputSearchModelType | undefined>;
  onSelectionChange: (selections: TreeSelectionType[]) => void;
}) {
  const model = computed(() => new TreeFilterSelection(deps.fieldOptions.value ?? []));

  const display = ref<{ options: FilterDefinitionValue[]; expanded: boolean }>({ options: [], expanded: false });
  const checkboxTreeDataRef = ref<CheckBoxTreeModelType[]>([]);
  const selections = ref<TreeSelectionType[]>([]);
  const nbSelectedCheckboxTreeItems = computed(() => model.value.countSelectedLeaves(selections.value));

  const initialize = () => {
    const options = deps.fieldOptions.value;

    if (!options) {
      return;
    }

    selections.value = model.value.sanitize(selections.value.length > 0 ? selections.value : deps.initialValue.value);

    // Options can change while searching (search-mandatory filters): the search results stay displayed, collapsed.
    const searchResults = deps.searchData.value;

    if (isSearchActive(deps.searchInput.value) && searchResults?.length) {
      rebuildTree(searchResults, { expanded: false });
    } else {
      rebuildTree(options, { expanded: false });
    }
  };

  const rebuildTree = (options: FilterDefinitionValue[], opts: { expanded: boolean }) => {
    display.value = { options, expanded: opts.expanded };
    checkboxTreeDataRef.value = model.value.buildCheckBoxTree(options, selections.value, opts);
  };

  /**
   * Bound to the CheckBoxTree `update-data` event: the component has already cascaded the user's click.
   */
  const onTreeUpdate = ({ data: tree }: CheckBoxTreeUpdateDataEvent) => {
    commitSelections([...hiddenSelections(tree), ...model.value.readSelections(tree)]);
  };

  const selectAll = () => replaceDisplayedSelections(model.value.listSelectAllTargets(checkboxTreeDataRef.value));

  const unselectAll = () => replaceDisplayedSelections([]);

  const reset = () => {
    checkboxTreeDataRef.value = [];
    commitSelections([]);
  };

  const replaceDisplayedSelections = (targets: TreeSelectionType[]) => {
    commitSelections([...hiddenSelections(checkboxTreeDataRef.value), ...targets]);

    if (display.value.options.length > 0) {
      rebuildTree(display.value.options, { expanded: display.value.expanded });
    }
  };

  /**
   * What the displayed tree does not cover, and must therefore survive the user's change.
   */
  const hiddenSelections = (tree: CheckBoxTreeModelType[]) => {
    const split = model.value.expandForDisplay(selections.value, display.value.options);

    return model.value.partitionByVisibility(split, tree).hidden;
  };

  const commitSelections = (next: TreeSelectionType[]) => {
    selections.value = next;
    deps.onSelectionChange(next);
  };

  watch(
    [deps.fieldOptions, deps.isEnabled],
    () => {
      if (deps.isEnabled.value) {
        initialize();
      }
    },
    { immediate: true }
  );

  watch(deps.searchData, (results) => {
    if (!results) {
      return;
    }

    if (results.length > 0) {
      rebuildTree(results, { expanded: false });
    } else {
      checkboxTreeDataRef.value = [];
    }
  });

  watch(
    deps.searchInput,
    (input) => {
      const options = deps.fieldOptions.value;
      if (!isSearchActive(input) && options) {
        rebuildTree(options, { expanded: false });
      }
    },
    { deep: true }
  );

  return {
    reset,
    initialize,
    selectAll,
    unselectAll,
    onTreeUpdate,
    checkboxTreeDataRef,
    nbSelectedCheckboxTreeItems
  };
}
